<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <dialog ref="dialogRef" role="dialog" aria-modal="true">
    <div class="content-wrapper">
      <div ref="contentRef" class="content" v-bind="$attrs">
        <AppCloseButton v-if="showCloseButton" class="close" :on-close="hideModal" />
        <AppModalMain>
          <slot />
        </AppModalMain>
        <div class="row">
          <slot name="footer" />
          <AppTimer :key="timerKey" :timeout="timeout" :callback="hideModal" />
        </div>
        <AppModalBusyOverlay :is-busy :busy-message />
      </div>
    </div>
  </dialog>
</template>

<script setup>
import {onClickOutside} from "@vueuse/core";
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import AppCloseButton from "@/components/App/CloseButton";
import AppTimer from "@/components/App/Timer";
import AppModalMain from "@/components/App/Modal/Main";
import AppModalBusyOverlay from "@/components/App/Modal/Overlay";
import {useMatchingHash} from "@/composables/useMatchingHash";

const props = defineProps([
  "backgroundColor",
  "hash",
  "timeout",
  "openDelay",
  "noCloseButton",
  "isBusy",
  "busyMessage",
]);
const log = (msg) => process.env.VUE_APP_DEV && console.log(`${props.hash}: ${msg}`);
const emit = defineEmits(["closed"]);
const [dialogRef, timerKey] = [ref(null), ref(0)];
const [route, router] = [useRoute(), useRouter()];
const contentRef = ref(null);
const hashString = computed(() => route.hash.slice(1));
const hasMatchingHash = useMatchingHash(props, route);

defineOptions({inheritAttrs: false});

const match = computed(() => {
  if (!props.hash) return false;
  const currentHash = route.hash.slice(1);
  if (!currentHash) return false;
  if (typeof props.hash === "string") return currentHash === props.hash;
  if (props.hash instanceof RegExp) return props.hash.test(currentHash);
  return false;
});

const showCloseButton = computed(() => {
  if (hasMatchingHash.value) log(`showCloseButton()`);
  if (hasMatchingHash.value) log(`noCloseButton = ${props.noCloseButton}`);
  return props.noCloseButton === undefined;
});

async function checkHash() {
  if (hasMatchingHash.value)
    log(
      `[checkHash] triggered with route.hash = '${route.hash}', hashString = '${hashString.value}'`
    );
  if (!props.hash) {
    if (hasMatchingHash.value) log(`[checkHash] props.hash is not defined → skipping`);
    return;
  }
  if (!match.value) {
    const modal = dialogRef?.value;
    if (modal?.open) {
      if (hasMatchingHash.value) log(`Mismatch and modal is open → calling hideModal()`);
      hideModal();
    } else {
      if (hasMatchingHash.value) log(`[checkHash] Mismatch but modal is not open → no action`);
    }
    return;
  }
  if (props.openDelay !== undefined) {
    const delay = 1000 * props.openDelay;
    if (hasMatchingHash.value)
      log(`[checkHash] openDelay = ${props.openDelay}s → delaying showModal() by ${delay}ms`);
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
  if (hasMatchingHash.value) log(`[checkHash] Match found → showing modal`);
  showModal();
}

function showModal() {
  if (hasMatchingHash.value) log(`[showModal] called`);
  document.body.style.overflow = "hidden";
  const modal = dialogRef?.value;
  if (!modal) return console.error("Modal: dialogRef not defined.");
  modal.showModal();
  timerKey.value++;
  if (hasMatchingHash.value) log(`[showModal] modal shown, timerKey = ${timerKey.value}`);
}

function hideModal() {
  if (hasMatchingHash.value) log(`[hideModal] called`);
  document.body.style.overflow = "";
  const modal = dialogRef?.value;
  if (!modal) return;
  if (!modal.open) return;
  modal.close();
  emit("closed");
  const currentHash = route.hash.startsWith("#") ? route.hash.slice(1) : "";
  const isExactMatch = typeof props.hash === "string" && currentHash === props.hash;
  const isPatternMatch = props.hash instanceof RegExp && props.hash.test(currentHash);
  if (hasMatchingHash.value)
    log(
      `[hideModal] currentHash = '${currentHash}', props.hash = '${props.hash}', ` +
        `exactMatch = ${isExactMatch}, patternMatch = ${isPatternMatch}`
    );
  if ((isExactMatch || isPatternMatch) && route.hash !== "") {
    if (hasMatchingHash.value) log(`[hideModal] hash matched → removing hash from route`);
    router.replace({path: route.path, hash: ""});
  } else {
    if (hasMatchingHash.value) log(`[hideModal] no hash removal needed`);
  }
}

onClickOutside(contentRef, hideModal);

onMounted(async () => {
  if (hasMatchingHash.value)
    log(`[onMounted] modal component mounted with props.hash = '${props.hash}'`);
  if (props.timeout && hasMatchingHash.value) log(`[onMounted] timeout = ${props.timeout}`);
  await nextTick();
  checkHash();
});

watch(
  () => route.hash,
  (newHash, oldHash) => {
    if (newHash === oldHash) return;
    if (hasMatchingHash.value) log(`[watch:route.hash] changed from '${oldHash}' to '${newHash}'`);
    checkHash();
  }
);
</script>

<style scoped>
dialog {
  position: fixed;
  pointer-events: auto;
  border: none;
  width: 100dvw;
  height: 100dvh;
  padding: 0;
  margin: 0;
  transform: translate(-50%, -50%);
  z-index: 100;
  inset: 50%;
  background-color: rgba(0, 0, 0, 0.005);
  backdrop-filter: blur(1px);
}
dialog::backdrop {
  background-color: rgba(0, 0, 0, 0.75) !important;
  backdrop-filter: blur(1px);
}
.content-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  max-height: 100dvh;
  overflow: auto;
}
.content {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 320px;
  width: clamp(320px, 90vw, 380px);
  max-height: min(700px, 95dvh);
  overflow: hidden;
  background: #e4e4e4;
  border-radius: 12px;
  padding: 0.75rem 1rem 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  color: #333;
}
.content .close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 100;
}
.content button {
  pointer-events: auto;
}
.content :deep(button[type="submit"]) {
  background: #3ddc97;
  color: #fff;
  font-weight: bold;
  border: none;
  box-shadow: none;
  width: 100%;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  font-size: 0.875rem;
  line-height: 1;
  cursor: pointer;
  margin-top: 1.5rem;
  display: block;
}
.content :deep(button[type="submit"]:disabled) {
  background: #aaa;
  color: #fff;
  opacity: 0.6;
  cursor: not-allowed;
}
.content :deep(.footer-buttons) {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
}
.content :deep(.footer-buttons button) {
  flex: 1;
  border-radius: 999px;
  margin-top: 0;
  width: auto;
  padding: 0.4rem 1rem;
  font-size: 0.875rem;
  line-height: 1;
}
.content :deep(.footer-buttons button[type="button"]) {
  background: #cdcdcd;
  color: #333;
  border: none;
  box-shadow: none;
  font-weight: normal;
}
.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0;
}
.row > * {
  flex: 0 1 auto;
}
.row > *:first-child {
  margin-right: 1rem;
}
.row > *:last-child {
  text-align: right;
}
.row:has(> :only-child) {
  justify-content: flex-end;
}
</style>
